/**
 * Historia 138 — the grammar of a custom language code, as pure functions.
 *
 * A custom language is a standard one plus a suffix: `es-acme` inherits from `es`. Everything else
 * in this story leans on being able to split that apart, and to do it **before any answer has
 * arrived** -- the base is needed to pick a date locale on the first paint, and it has to keep
 * working when the catalogue request fails [CD-3]. So this module knows nothing about the network
 * and nothing about the catalogue: it is string arithmetic, and that is deliberate.
 *
 * Three ideas that are easy to confuse and must not be:
 *   - **selected code** -- what the user picked and what is saved against them.
 *   - **content code** -- the language the interface actually runs on. With a custom language that
 *     replaces its base, the two differ: the user keeps `es` saved while the interface runs
 *     `es-acme` [CD-4].
 *   - **locale code** -- the base prefix, which is what date formats and every other non-textual
 *     inheritance resolve by [DG1-1]. Whoever reads the active language to choose a locale must read
 *     its base part instead.
 */

/** Separator between the base language and the customer suffix. The code is `<base>-<suffix>`. */
const CUSTOM_SUFFIX_SEPARATOR = '-'

/**
 * The standard language a code inherits from, which for a standard code is the code itself.
 *
 * Pure on purpose: no catalogue, no request. The catalogue publishes `baseLanguageCode` too and it
 * is redundant with the prefix -- deliberately, so that whoever has no catalogue can still derive
 * it.
 *
 * @param {string} code Language code, custom (`es-acme`) or standard (`es`)
 * @returns {string} The base code (`es`), or '' when there is no usable code
 */
export const baseLanguageCodeOf = (code) => {
  if (typeof code !== 'string' || code === '') {
    return ''
  }
  const separatorAt = code.indexOf(CUSTOM_SUFFIX_SEPARATOR)
  return separatorAt === -1 ? code : code.slice(0, separatorAt)
}

/**
 * Whether a code carries a customer suffix.
 *
 * ⚠️ This answers a question about the SHAPE of the code, not about the catalogue. A code shaped
 * like a custom one that nobody registered is still not a language; only the catalogue knows that.
 * It exists for the places that have to decide before the catalogue is there.
 *
 * @param {string} code Language code
 * @returns {boolean} True when the code has a base and a suffix
 */
export const isCustomLanguageCode = (code) => {
  const base = baseLanguageCodeOf(code)
  return base !== '' && base !== code
}

/**
 * The code every non-textual inheritance resolves by: date formats, the date-fns locale, and the
 * rest of the eight places of RN30. A custom language adds no locale configuration of its own -- it
 * inherits the one of its base [DG1-1].
 *
 * @param {string} code Active language code
 * @returns {string} The code to look a locale up with
 */
export const localeCodeOf = (code) => baseLanguageCodeOf(code)

/**
 * The language the interface must run on, given what the user chose and what the catalogue says.
 *
 * Almost always this is simply what the user chose. It differs in one case, and that case is the
 * subtle one [RN15/CA4]: an installation may register a custom language that **replaces** a standard
 * one, and then a user who chose `es` must see `es-acme` without their saved choice being touched.
 * The corollary is wanted: withdraw the replacement tomorrow and that user is back on standard `es`
 * with nobody having edited their configuration.
 *
 * @param {string} selectedCode The code the user chose, as saved against them
 * @param {Array<Object>} catalogue Entries of `GET /endpoints/languages`
 * @returns {string} The code to apply to the interface
 */
export const contentCodeFor = (selectedCode, catalogue = []) => {
  if (!Array.isArray(catalogue)) {
    return selectedCode
  }
  const replacement = catalogue.find((language) =>
    language?.replacesBase === true && language?.baseLanguageCode === selectedCode
  )
  return replacement?.code || selectedCode
}

/**
 * Whether the content of a code has to be fetched at all.
 *
 * The eight standard dictionaries travel inside the application, so asking for them would be a
 * request that buys nothing. An installation that registers no custom language therefore makes not
 * one extra call, and its start-up is byte for byte the one it had before this story [CA13/RN22].
 *
 * @param {string} contentCode The code the interface will run on
 * @returns {boolean} True when the dictionary has to be requested
 */
export const needsFetchedContent = (contentCode) => isCustomLanguageCode(contentCode)
